export interface PlacementDetectionConfig {
  /** The id attribute to match on the sibling <feature-app> element */
  siblingId: string;
  /** Which sibling to check relative to our FA in DOM order */
  direction: 'previous' | 'next';
}

export interface PlacementResult {
  isAdjacent: boolean;
  siblingId: string | null;
  ourIndex: number;
  totalFeatureApps: number;
}

const DEFAULT_CONFIG: PlacementDetectionConfig = {
  siblingId: 'b373ebe8-46bc-4942-b1b9-078ea99a36ba-1',
  direction: 'previous',
};

/**
 * Detects whether our Feature App is placed adjacent to a specific sibling FA
 * by inspecting the DOM order of all <feature-app> elements on the page.
 *
 * Matches on the stable `id` attribute rather than `base-url`, since base-url
 * contains a version number that changes across deployments.
 *
 * This bypasses wrapper <div>s between FAs by querying all <feature-app> elements
 * from document.body rather than relying on direct DOM sibling relationships.
 */
export function detectPlacement(
  elementRef: HTMLElement | null,
  config: Partial<PlacementDetectionConfig> = {},
): PlacementResult {
  const { siblingId, direction } = { ...DEFAULT_CONFIG, ...config };

  const noMatch: PlacementResult = {
    isAdjacent: false,
    siblingId: null,
    ourIndex: -1,
    totalFeatureApps: 0,
  };

  if (!elementRef) return noMatch;

  // Walk up to our <feature-app> ancestor
  const ourFeatureApp = elementRef.closest('feature-app');
  if (!ourFeatureApp) return noMatch;

  // Query all <feature-app> elements on the page from a common ancestor
  const allFeatureApps = Array.from(document.body.querySelectorAll('feature-app'));
  const ourIndex = allFeatureApps.indexOf(ourFeatureApp);

  if (ourIndex === -1) {
    return { ...noMatch, totalFeatureApps: allFeatureApps.length };
  }

  const siblingIndex = direction === 'previous' ? ourIndex - 1 : ourIndex + 1;
  if (siblingIndex < 0 || siblingIndex >= allFeatureApps.length) {
    return { ...noMatch, ourIndex, totalFeatureApps: allFeatureApps.length };
  }
  const foundSiblingId = allFeatureApps[siblingIndex].getAttribute('id') ?? null;

  return {
    isAdjacent: foundSiblingId === siblingId,
    siblingId: foundSiblingId,
    ourIndex,
    totalFeatureApps: allFeatureApps.length,
  };
}
